'use client';

import { useEffect, useMemo, useState } from 'react';
import type { FAQBlock } from '@impact/types';
import { cn } from '@/lib/utils';
import { ArrowRight, ChevronDown, Minus, Plus } from 'lucide-react';
import { RichtextTiptap } from '@/components/page-editor/v2/canvas/RichtextTiptap';

interface FAQBlockRendererProps {
  block: FAQBlock;
  editing?: boolean;
}

const DENSITY_PADDING = {
  compact: 'p-3',
  default: 'p-5',
  loose: 'p-6',
} as const;

const DENSITY_GAP = {
  compact: 'space-y-2',
  default: 'space-y-3',
  loose: 'space-y-4',
} as const;

function AccordionIcon({
  kind,
  isOpen,
}: {
  kind: NonNullable<FAQBlock['data']['accordionIcon']>;
  isOpen: boolean;
}) {
  if (kind === 'none') return null;
  if (kind === 'plus') {
    return isOpen ? (
      <Minus className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
    ) : (
      <Plus className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
    );
  }
  if (kind === 'arrow') {
    return (
      <ArrowRight
        className={cn(
          'h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-200',
          isOpen && 'rotate-90',
        )}
      />
    );
  }
  return (
    <ChevronDown
      className={cn(
        'h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-200',
        isOpen && 'rotate-180',
      )}
    />
  );
}

export function FAQBlockRenderer({ block, editing }: FAQBlockRendererProps) {
  const {
    title,
    subtitle,
    items,
    style = 'accordion',
    accordionStyle = 'bordered',
    accordionIcon = 'chevron',
    allowMultipleOpen = false,
    defaultOpen = 'none',
    numbered = false,
    density = 'default',
  } = block.data;

  const initialOpen = useMemo(() => {
    if (defaultOpen === 'all') return new Set(items.map((i) => i.id));
    if (defaultOpen === 'first' && items[0]) return new Set([items[0].id]);
    return new Set<string>();
  }, [defaultOpen, items]);

  const [openItems, setOpenItems] = useState<Set<string>>(initialOpen);

  useEffect(() => {
    setOpenItems(initialOpen);
  }, [initialOpen]);

  const toggleItem = (id: string) => {
    setOpenItems((prev) => {
      if (allowMultipleOpen) {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }
      return prev.has(id) ? new Set() : new Set([id]);
    });
  };

  const itemPadding = DENSITY_PADDING[density];
  const itemGap = DENSITY_GAP[density];

  if (style === 'list') {
    return (
      <div className="my-12">
        {title && (
          <h2
            className="text-2xl md:text-3xl font-bold text-foreground mb-2"
            {...(editing ? { 'data-editable': 'data.title' } : {})}
          >
            {title}
          </h2>
        )}
        {subtitle && (
          <p
            className="text-muted-foreground mb-8"
            {...(editing ? { 'data-editable': 'data.subtitle' } : {})}
          >
            {subtitle}
          </p>
        )}
        <div className={cn('space-y-8', itemGap)}>
          {items.map((item, i) => (
            <div key={item.id} {...(editing ? { 'data-block-item': String(i) } : {})}>
              <h3
                className="inline-block min-w-[6rem] text-lg font-semibold text-foreground mb-2 empty:before:text-muted-foreground/50 empty:before:content-['Question']"
                {...(editing ? { 'data-editable': `data.items[${i}].question` } : {})}
              >
                {numbered ? `${i + 1}. ` : ''}
                {item.question}
              </h3>
              {editing ? (
                <RichtextTiptap
                  blockId={block.id}
                  initialHtml={item.answer}
                  path={`data.items[${i}].answer`}
                />
              ) : (
                <div
                  className="prose prose-sm max-w-none text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: item.answer }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Accordion style
  const containerClass =
    accordionStyle === 'separated'
      ? cn('flex flex-col', itemGap)
      : accordionStyle === 'borderless'
        ? 'divide-y divide-border'
        : 'divide-y divide-border rounded-xl border border-border overflow-hidden';

  const itemContainerClass =
    accordionStyle === 'separated'
      ? 'rounded-xl border border-border bg-background overflow-hidden'
      : 'bg-background';

  return (
    <div className="my-12">
      {title && (
        <h2
          className="text-2xl md:text-3xl font-bold text-foreground mb-2"
          {...(editing ? { 'data-editable': 'data.title' } : {})}
        >
          {title}
        </h2>
      )}
      {subtitle && (
        <p
          className="text-muted-foreground mb-8"
          {...(editing ? { 'data-editable': 'data.subtitle' } : {})}
        >
          {subtitle}
        </p>
      )}
      <div className={containerClass}>
        {items.map((item, i) => {
          const isOpen = editing || openItems.has(item.id);
          return (
            <div
              key={item.id}
              className={itemContainerClass}
              {...(editing ? { 'data-block-item': String(i) } : {})}
            >
              {editing ? (
                <div className={cn('flex items-center justify-between', itemPadding)}>
                  <span
                    className="inline-block min-w-[6rem] pr-4 font-medium text-foreground empty:before:text-muted-foreground/50 empty:before:content-['Question']"
                    data-editable={`data.items[${i}].question`}
                  >
                    {numbered ? `${i + 1}. ` : ''}
                    {item.question}
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => toggleItem(item.id)}
                  className={cn(
                    'w-full flex items-center justify-between text-left hover:bg-muted/50 transition-colors',
                    itemPadding,
                  )}
                >
                  <span className="font-medium text-foreground pr-4">
                    {numbered ? `${i + 1}. ` : ''}
                    {item.question}
                  </span>
                  <AccordionIcon kind={accordionIcon} isOpen={isOpen} />
                </button>
              )}
              <div
                className={cn(
                  'overflow-hidden transition-all duration-200',
                  isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0',
                )}
              >
                {editing ? (
                  <div className={cn('pt-0', itemPadding)}>
                    <RichtextTiptap
                      blockId={block.id}
                      initialHtml={item.answer}
                      path={`data.items[${i}].answer`}
                    />
                  </div>
                ) : (
                  <div
                    className={cn(
                      'pt-0 prose prose-sm max-w-none text-muted-foreground',
                      itemPadding,
                    )}
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
